import { useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';
import { extractDate, formatTime12, getUserName, getUserInitials, getEntryColor, ENTRY_TYPE_LABELS, type ScheduleUser } from './scheduleHelpers';

interface WeekViewProps { weekStart: Date; entries: ScheduleEntry[]; users: ScheduleUser[]; onEntryClick: (entry: ScheduleEntry) => void; }

function fmtDate(d: Date): string { return d.toISOString().split('T')[0]; }
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const STATUS_DOT: Record<string, string> = { completed: palette.green, in_progress: palette.blue, cancelled: palette.g300, scheduled: palette.accent };

function DraggableEntry({ entry, onEntryClick }: { entry: ScheduleEntry; onEntryClick: (e: ScheduleEntry) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: entry.id, data: { entry } });
  const color = getEntryColor(entry);
  const label = ENTRY_TYPE_LABELS[entry.type as keyof typeof ENTRY_TYPE_LABELS] ?? '';
  const dotColor = STATUS_DOT[entry.status] ?? palette.g300;
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={() => { if (!isDragging) onEntryClick(entry); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 4, width: '100%',
        padding: '3px 6px', marginBottom: 2, borderRadius: 4, border: 'none',
        cursor: isDragging ? 'grabbing' : 'grab', fontFamily: 'inherit', textAlign: 'left',
        background: color + '14', borderLeft: `2px solid ${color}`,
        opacity: isDragging ? 0.3 : 1, touchAction: 'none',
      }}>
      <div style={{ width: 5, height: 5, borderRadius: 3, background: dotColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: palette.g800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title}</div>
        <div style={{ fontSize: 9.5, color: palette.g500 }}>{entry.all_day ? 'All day' : formatTime12(entry.start_at)} · {label}</div>
      </div>
    </div>
  );
}

function DroppableCell({ id, isToday, children }: { id: string; isToday: boolean; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <td ref={setNodeRef} style={{
      padding: '4px 4px', borderBottom: `1px solid ${palette.g100}`, verticalAlign: 'top',
      background: isOver ? palette.accentWash : isToday ? palette.accentWash + '60' : 'transparent',
      borderLeft: `1px solid ${palette.g100}`, transition: 'background 0.15s',
    }}>
      {children}
    </td>
  );
}

export function WeekView({ weekStart, entries, users, onEntryClick }: WeekViewProps) {
  const dates = useMemo(() => { const r: string[] = []; for (let i = 0; i < 7; i++) { const d = new Date(weekStart); d.setDate(d.getDate() + i); r.push(fmtDate(d)); } return r; }, [weekStart]);
  const todayStr = useMemo(() => fmtDate(new Date()), []);

  const entryIndex = useMemo(() => {
    const map = new Map<string, ScheduleEntry[]>();
    for (const e of entries) {
      if (e.status === 'cancelled') continue;
      const ds = extractDate(e.start_at); const uid = e.assigned_to?.[0] ?? '__unassigned';
      const key = `${uid}-${ds}`; const arr = map.get(key) ?? []; arr.push(e); map.set(key, arr);
    }
    return map;
  }, [entries]);

  const staffIds = useMemo(() => {
    const ids = new Set<string>();
    for (const u of users) ids.add(u.id);
    for (const e of entries) { if (e.status === 'cancelled') continue; if (!e.assigned_to?.length) ids.add('__unassigned'); }
    return Array.from(ids);
  }, [entries, users]);

  const TH: React.CSSProperties = { padding: '10px 8px', textAlign: 'center', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', borderBottom: `1px solid ${palette.g150}`, textTransform: 'uppercase' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 160, textAlign: 'left', color: palette.g400, paddingLeft: 12 }}>Team</th>
            {dates.map((ds, i) => {
              const d = new Date(ds + 'T12:00:00'); const isToday = ds === todayStr;
              return (
                <th key={ds} style={{ ...TH, color: isToday ? palette.accent : palette.g400, background: isToday ? palette.accentWash : 'transparent' }}>
                  <div>{DAY_NAMES[i]}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: isToday ? palette.accent : palette.g700, marginTop: 2 }}>{d.getDate()}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {staffIds.map(uid => {
            const name = uid === '__unassigned' ? 'Unassigned' : getUserName(uid, users);
            const initials = uid === '__unassigned' ? '?' : getUserInitials(uid, users);
            return (
              <tr key={uid} onMouseEnter={e => (e.currentTarget.style.background = palette.g50)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <td style={{ padding: '8px 12px', borderBottom: `1px solid ${palette.g100}`, fontSize: 13, fontWeight: 500, color: palette.g800 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 14, background: palette.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: palette.white, flexShrink: 0 }}>{initials}</div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  </div>
                </td>
                {dates.map(ds => {
                  const cellEntries = entryIndex.get(`${uid}-${ds}`) ?? [];
                  const isToday = ds === todayStr;
                  return (
                    <DroppableCell key={ds} id={`week|${uid}|${ds}`} isToday={isToday}>
                      {cellEntries.slice(0, 3).map(entry => (
                        <DraggableEntry key={entry.id} entry={entry} onEntryClick={onEntryClick} />
                      ))}
                      {cellEntries.length > 3 && <div style={{ fontSize: 10, color: palette.g400, paddingLeft: 6, marginTop: 2 }}>+{cellEntries.length - 3} more</div>}
                    </DroppableCell>
                  );
                })}
              </tr>
            );
          })}
          {staffIds.length === 0 && (
            <tr><td colSpan={8} style={{ padding: '32px 0', textAlign: 'center', color: palette.g400, fontSize: 13.5 }}>No team members found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
